import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import '../../estilos/confirmacion-general.css';

/*
 * COMPONENTE UNICO DE CONFIRMACION DEL NUCLEO COMPARTIDO
 * (RF-166, D-277, D-278a, RN-495 a RN-505; contrato de uso en §7 bis del analisis
 * `analisis/requerimientos-categorias.md` rev. 4).
 *
 * POR QUE EXISTE. H-09: "Desactivar no hace nada". El boton si funcionaba y el
 * listado si se refrescaba; lo que fallaba es que la confirmacion se dibujaba como
 * una tarjeta mas del flujo del documento, DESPUES de toda la lista, sin posicion
 * fija, sin cortina y sin mover el foco. Con el catalogo completo eso la dejaba a
 * mas de 900 px por debajo de lo que la persona esta mirando, contra unos 740 px de
 * alto visible: literalmente no pasaba nada EN LA PARTE DE LA PANTALLA QUE SE MIRA.
 * Y el renglon de dos lineas de este mismo incremento (RF-165) alarga la lista a
 * unos 1 640 px, asi que la posicion fija no es una preferencia estetica: sin ella
 * el arreglo de H-06 empeora H-09 (RN-493, RN-509).
 *
 * ES GENERICO, Y ESO ES NORMATIVO (RN-495). No sabe nada de categorias: no habla
 * con el servidor, no conoce objetos de dominio, no decide nada de negocio y no
 * ejecuta la operacion. Recibe texto ya resuelto en castellano y avisa que eligio
 * la persona. En ESTE incremento lo consume solo la pantalla de Categorias (D-279);
 * Monedas y Usuarios del hogar lo adoptaran en su propio incremento SIN
 * modificarlo, y por eso esta escrito asi desde el primer dia: construirlo para una
 * sola pantalla y "generalizarlo despues" es exactamente como nacieron las tres
 * copias que este incremento viene a evitar (RNF-068).
 *
 * VERIFICACION DE CONSTRUCCION QUE PEDIA RN-503 / S-10-10, hecha y con resultado:
 * el menu de tipos de movimiento del incremento 09
 * (`nucleo/navegacion/MenuDeTiposDeMovimiento.jsx:70-100`) NO expone ninguna capa
 * de superposicion reutilizable —su velo, su hoja y su caja son tres objetos de
 * estilo en linea privados de ese archivo, sin componente ni modulo aparte—, asi
 * que no hay nada que reusar y esta pieza se escribe completa. Lo que NO se hace,
 * porque seria exactamente la enfermedad que se esta curando: extraer una segunda
 * capa de superposicion "general" a medias, ni tocar ese menu, que esta fuera de la
 * lista cerrada de D-278.
 *
 * CUATRO SITUACIONES, Y NO HAY UNA QUINTA DE ERROR (§7 bis). Cerrada, abierta, en
 * curso y cerrandose. El error de la operacion lo presenta LA PANTALLA con el aviso
 * unico del 08 (RF-139, RN-505): meterlo aqui dentro seria tener dos avisos de
 * error distintos, que es lo que el incremento 08 vino a unificar.
 */

/** Tono del boton de aceptar. Por omision, DESTRUCTIVA (§7 bis). */
export const TONOS_DE_CONFIRMACION = Object.freeze({
  DESTRUCTIVA: 'DESTRUCTIVA',
  NEUTRAL: 'NEUTRAL',
});

/** Rotulo del boton de cancelar por omision (§7 bis). */
export const ROTULO_DE_CANCELAR_POR_OMISION = 'Cancelar';

/**
 * Rotulo del boton de aceptar mientras la operacion va en camino, cuando el
 * consumidor no pasa uno propio. Es el del dibujo de la pieza suelta
 * (`prototipo-v28-componente-confirmacion.html`); Categorias pasa "Desactivando…".
 */
export const ROTULO_EN_CURSO_POR_OMISION = 'Un momento…';

/** Lo que puede recibir el foco dentro del panel, para no dejarlo escapar atras. */
const ENFOCABLES = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @param abierta         si la confirmacion se muestra. La pantalla manda: el
 *                        componente NUNCA decide abrirse ni cerrarse solo.
 * @param pregunta        el titulo, en forma de pregunta y nombrando el objeto.
 * @param consecuencia    que pasa si se acepta. Texto ya resuelto en castellano.
 * @param detalle         [{ clave, texto, dato }] renglones de apoyo. El
 *                        componente LOS PINTA, NO LOS CALCULA.
 * @param children        [NUEVO 2026-10-07 · incremento 12/16] CONTENIDO LIBRE que
 *                        la pantalla necesita DENTRO de la pregunta: hoy, el campo
 *                        del motivo opcional de «eliminar un movimiento» (RF-204).
 *                        Va ENTRE el desglose y la advertencia, que es el orden del
 *                        prototipo v30. Omitirlo no dibuja nada: ni caja, ni hueco.
 * @param advertencia     aviso adicional cuando lo mostrado esta incompleto.
 * @param rotuloDeAceptar texto del boton que confirma. NOMBRA LA ACCION:
 *                        "Si, desactivar", nunca "Aceptar".
 * @param rotuloDeCancelar texto del boton que rechaza. Por omision, "Cancelar".
 * @param tono            DESTRUCTIVA (por omision) o NEUTRAL.
 * @param enCurso         si la operacion ya se esta ejecutando: el boton de
 *                        aceptar deja de admitir un segundo toque (RN-499).
 * @param rotuloEnCurso   que dice el boton de aceptar mientras va en camino.
 * @param alAceptar       la pantalla EJECUTA la operacion. El componente no la
 *                        ejecuta y no se cierra por su cuenta.
 * @param alCancelar      cancelar, tocar la cortina o pulsar escape. Las tres
 *                        cosas significan lo mismo y NUNCA aceptar (RN-499).
 */
export function ConfirmacionGeneral({
  abierta,
  pregunta,
  consecuencia,
  detalle = null,
  /*
   * RANURA DE CONTENIDO · [NUEVO 2026-10-07 · incremento 12/16]
   * AUTORIZADA EXPRESAMENTE por Deisy Paez ("Respuesta 1: autorizo") para tocar el
   * nucleo compartido, porque la pregunta de eliminar un movimiento lleva DENTRO un
   * campo de captura —el motivo opcional de RF-204— y el prototipo v30 lo dibuja
   * sobre fondo normal, no metido en la caja ambar de la advertencia.
   *
   * ES ADITIVA Y NO CAMBIA NADA DE LO QUE YA EXISTE: su valor por omision es nulo,
   * no se dibuja ningun envoltorio cuando falta, y las dos pantallas que ya
   * consumen esta pieza —Categorias y EditarCuenta— no la pasan, asi que renderizan
   * EXACTAMENTE el mismo arbol que antes.
   *
   * LO QUE ESTA RANURA NO ES: una puerta para que cada pantalla se dibuje su propia
   * confirmacion por dentro. La pieza sigue decidiendo el marco entero —cortina,
   * posicion, foco, escape y los dos botones— y sigue sin saber nada de negocio
   * (RN-495). Lo que entra aqui es contenido de la pantalla, y la pantalla responde
   * por el: el componente no lo valida, no lo lee y no lo envia a ninguna parte.
   *
   * VA ENTRE EL DESGLOSE Y LA ADVERTENCIA, y ese sitio es el del prototipo: primero
   * que pasa, luego lo que hay que decidir, y al final el aviso de que no tiene
   * vuelta, pegado a los botones donde se lee justo antes de pulsar.
   */
  children = null,
  advertencia = null,
  rotuloDeAceptar,
  rotuloDeCancelar = ROTULO_DE_CANCELAR_POR_OMISION,
  tono = TONOS_DE_CONFIRMACION.DESTRUCTIVA,
  enCurso = false,
  rotuloEnCurso = ROTULO_EN_CURSO_POR_OMISION,
  alAceptar = null,
  alCancelar = null,
  datoDePrueba = 'confirmacion-general',
}) {
  const panel = useRef(null);
  const botonDeAceptar = useRef(null);
  const origenDelFoco = useRef(null);

  /*
   * RN-499c · Salir equivale a cancelar, nunca a aceptar. Y mientras la operacion
   * va en camino no se sale: cerrar a media escritura dejaria a la persona sin
   * saber si ocurrio o no.
   */
  const salir = useCallback(() => {
    if (enCurso) return;
    alCancelar?.();
  }, [enCurso, alCancelar]);

  /*
   * RN-497 · El foco entra al abrir y VUELVE al elemento desde el que se abrio al
   * cerrar. En una lista de veintiun renglones eso es la diferencia entre seguir
   * donde estabas y tener que buscar tu sitio otra vez.
   *
   * El elemento de origen se guarda en el mismo efecto que devuelve el foco, para
   * que no haya forma de guardar uno y devolverlo a otro.
   */
  useEffect(() => {
    if (!abierta) return undefined;

    origenDelFoco.current = document.activeElement;
    botonDeAceptar.current?.focus();

    return () => {
      const volverA = origenDelFoco.current;
      origenDelFoco.current = null;
      // Si el elemento de origen desaparecio del documento —el renglon que se
      // acaba de desactivar, por ejemplo— no se fuerza nada: el navegador deja el
      // foco en el cuerpo y la pantalla sigue siendo usable.
      if (volverA && typeof volverA.focus === 'function' && document.contains(volverA)) {
        volverA.focus();
      }
    };
  }, [abierta]);

  /*
   * RN-499c y RN-497 · Escape sale (= cancela) y el tabulador no se escapa al
   * contenido de atras mientras la pieza esta abierta.
   */
  useEffect(() => {
    if (!abierta) return undefined;

    const alTeclear = (evento) => {
      if (evento.key === 'Escape') { evento.preventDefault(); salir(); return; }
      if (evento.key !== 'Tab') return;

      const dentro = panel.current?.querySelectorAll(ENFOCABLES);
      if (!dentro || dentro.length === 0) return;

      const primero = dentro[0];
      const ultimo = dentro[dentro.length - 1];
      const activo = document.activeElement;

      if (!panel.current.contains(activo)) { evento.preventDefault(); primero.focus(); return; }
      if (evento.shiftKey && activo === primero) { evento.preventDefault(); ultimo.focus(); return; }
      if (!evento.shiftKey && activo === ultimo) { evento.preventDefault(); primero.focus(); }
    };

    document.addEventListener('keydown', alTeclear, true);
    return () => document.removeEventListener('keydown', alTeclear, true);
  }, [abierta, salir]);

  /*
   * RN-500 · Mientras la confirmacion esta abierta es el unico punto de atencion:
   * el contenido de atras no se desplaza por accidente al arrastrar sobre la
   * cortina. Se marca el cuerpo y la hoja de estilo hace el resto.
   */
  useEffect(() => {
    if (!abierta) return undefined;
    document.body.classList.add('confirmacion-abierta');
    return () => document.body.classList.remove('confirmacion-abierta');
  }, [abierta]);

  /* Situacion 1 · Cerrada: NO se dibuja nada. Ni cortina, ni hueco reservado. */
  if (!abierta) return null;

  const idDeLaPregunta = `${datoDePrueba}-pregunta`;
  const idDeLaConsecuencia = `${datoDePrueba}-consecuencia`;
  const renglones = detalle ?? [];

  /*
   * El portal es lo que la saca del flujo del documento (RN-496): la pieza deja de
   * ser "lo que viene despues de la lista" y pasa a colocarse respecto de la
   * VENTANA. Por eso se ve igual con la pagina arriba, en medio o abajo, y con una
   * lista de dos renglones o de doscientos (RN-498, RNF-094).
   */
  return createPortal(
    <>
      <div
        className="confirmacion__cortina"
        data-prueba={`${datoDePrueba}-cortina`}
        onClick={salir}
        /* La cortina no es un destino de teclado: escape ya hace lo mismo y
           anunciarla como control daria una parada vacia al lector de pantalla. */
        aria-hidden="true"
      />

      <div className="confirmacion__ventana" data-prueba={`${datoDePrueba}-ventana`}>
        <div
          ref={panel}
          className="confirmacion__panel"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={idDeLaPregunta}
          aria-describedby={idDeLaConsecuencia}
          aria-busy={enCurso ? 'true' : undefined}
          data-prueba={datoDePrueba}
          data-en-curso={enCurso ? 'si' : 'no'}
        >
          <h3 className="confirmacion__pregunta" id={idDeLaPregunta} data-prueba={`${datoDePrueba}-pregunta`}>
            {pregunta}
          </h3>

          <p
            className="confirmacion__consecuencia"
            id={idDeLaConsecuencia}
            data-prueba={`${datoDePrueba}-consecuencia`}
          >
            {consecuencia}
          </p>

          {renglones.length > 0 && (
            <div className="confirmacion__detalle" data-prueba={`${datoDePrueba}-detalle`}>
              {renglones.map((renglon, indice) => (
                <div
                  key={renglon?.clave ?? indice}
                  className="confirmacion__detalle-renglon"
                >
                  <span>{renglon?.texto}</span>
                  {renglon?.dato !== undefined && renglon?.dato !== null && (
                    <span><b>{renglon.dato}</b></span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Contenido de la pantalla. Sin envoltorio con estilo propio: lo que se
              pase manda su propia forma, y si no se pasa nada no se dibuja ni un
              elemento vacio (RN-496: ni cortina ni hueco reservado de mas). */}
          {children && (
            <div className="confirmacion__contenido" data-prueba={`${datoDePrueba}-contenido`}>
              {children}
            </div>
          )}

          {advertencia && (
            <div className="confirmacion__advertencia" data-prueba={`${datoDePrueba}-advertencia`}>
              {advertencia}
            </div>
          )}

          <div className="confirmacion__botones">
            <button
              ref={botonDeAceptar}
              type="button"
              className={`confirmacion__aceptar${tono === TONOS_DE_CONFIRMACION.NEUTRAL ? ' confirmacion__aceptar--neutro' : ''}`}
              /* RN-499 · Mientras la operacion va en camino, el boton de aceptar
                 no admite un segundo toque. */
              disabled={enCurso}
              onClick={() => alAceptar?.()}
              data-prueba={`${datoDePrueba}-aceptar`}
            >
              {enCurso && <span className="confirmacion__girando" aria-hidden="true" />}
              {enCurso ? rotuloEnCurso : rotuloDeAceptar}
            </button>

            <button
              type="button"
              className="confirmacion__cancelar"
              onClick={salir}
              disabled={enCurso}
              data-prueba={`${datoDePrueba}-cancelar`}
            >
              {rotuloDeCancelar}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default ConfirmacionGeneral;
